import { useState, useRef } from 'react';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ReservationModal({ isOpen, onClose }: ReservationModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((v, k) => body.append(k, v.toString()));

    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d72evt4mc4tbhfnc6dk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-cream rounded-3xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-saffron to-coral p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
          <h2 className="font-serif text-3xl text-white mb-1">Reserve Your Table</h2>
          <p className="font-sans text-sm text-white/80">Chaska Indian Kitchen – Duncan, SC</p>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal/10 mx-auto mb-4">
                <i className="ri-check-double-line text-teal text-3xl"></i>
              </div>
              <h3 className="font-serif text-2xl text-charcoal mb-2">Reservation Received!</h3>
              <p className="font-sans text-sm text-charcoal/60 mb-6">We'll confirm your table shortly. See you soon!</p>
              <button
                onClick={() => { setSubmitted(false); onClose(); }}
                className="bg-saffron text-white font-sans font-semibold text-sm px-8 py-3 rounded-full hover:bg-coral transition-colors cursor-pointer whitespace-nowrap"
              >
                Done
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">First Name</label>
                  <input
                    name="first_name"
                    type="text"
                    required
                    placeholder="Priya"
                    className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-saffron transition-colors"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Last Name</label>
                  <input
                    name="last_name"
                    type="text"
                    required
                    placeholder="Sharma"
                    className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-saffron transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Email</label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-saffron transition-colors"
                />
              </div>
              <div>
                <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Phone</label>
                <input
                  name="phone"
                  type="tel"
                  required
                  placeholder="(864) 555-0000"
                  className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-saffron transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Date</label>
                  <input
                    name="date"
                    type="date"
                    required
                    className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal focus:outline-none focus:border-saffron transition-colors"
                  />
                </div>
                <div>
                  <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Time</label>
                  <select
                    name="time"
                    required
                    className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal focus:outline-none focus:border-saffron transition-colors cursor-pointer"
                  >
                    <option value="">Select time</option>
                    {['11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM','5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM','8:30 PM'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Party Size</label>
                <select
                  name="party_size"
                  required
                  className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal focus:outline-none focus:border-saffron transition-colors cursor-pointer"
                >
                  <option value="">Select size</option>
                  {[1,2,3,4,5,6,7,8].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                  ))}
                  <option value="9+">9+ Guests</option>
                </select>
              </div>
              <div>
                <label className="font-sans text-xs font-medium text-charcoal/70 uppercase tracking-wide mb-1.5 block">Special Requests</label>
                <textarea
                  name="special_requests"
                  rows={3}
                  maxLength={500}
                  placeholder="Dietary restrictions, celebrations, seating preferences..."
                  className="w-full bg-white border border-charcoal/10 rounded-xl px-4 py-2.5 font-sans text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-saffron transition-colors resize-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-saffron to-coral text-white font-sans font-semibold text-sm py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Booking...' : 'Book My Table'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
